import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Center,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  Currency,
  NegotiationRoundStatus,
  SourcingEventV2Dto,
  type EvalBidDto,
} from "../../../../autogen/bff-api";
import { CompleteRoundButton } from "../negotiation/CompleteRoundButton";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { AwardBidsTable } from "./AwardBidsTable";
import { AwardDecisionHistory } from "./AwardDecisionHistory";
import { useAwardSelection } from "./AwardSelectionContext";
import {
  failsMinimumRequirement,
  hasUnassessedMinimumRequirements,
} from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { SupplierCountIndicator } from "./SupplierCountIndicator";

export const SelectWinner = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const { rounds } = useRoundSelection();
  // ADR-033 §5: awarding is blocked while any negotiation round is still open.
  const hasOpenRound = rounds.some((round) => round.status !== NegotiationRoundStatus.Completed);
  const completableRound = [...rounds]
    .filter((round) => round.status === NegotiationRoundStatus.Published && Date.parse(round.deadline) <= Date.now())
    .sort((a, b) => a.roundNumber - b.roundNumber)
    .at(-1);
  const {
    evaluation,
    isLoading,
    isError,
    winnerIds,
    toggleWinner,
    internalNote,
    setInternalNote,
    currency,
    setCurrency,
    amount,
    setAmount,
  } = useAwardSelection();
  const selectWinnerPublicCtx = event.eventContext.type === "Public" ? event.eventContext : null;
  const maxParticipants = selectWinnerPublicCtx?.frameworkMaxParticipants ?? undefined;

  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError || !evaluation) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load the evaluation.")}</Text>
      </Center>
    );
  }
  if (evaluation.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">{t("No delivered bids to award yet.")}</Text>
      </Center>
    );
  }

  return (
    <Stack
      spacing="6"
      bg="smBackground"
      rounded="xl"
      border="1px solid"
      borderColor="smBorder"
      p={{ base: "4", md: "6" }}
    >
      <AwardDecisionHistory event={event} />

      {hasOpenRound && (
        <Alert status="info" borderRadius="md" alignItems="center">
          <AlertIcon />
          <Text flex="1">{t("Complete all negotiation rounds before awarding")}</Text>
          {completableRound && <CompleteRoundButton round={completableRound} />}
        </Alert>
      )}

      <Stack spacing="2">
        <AwardBidsTable evaluation={evaluation} mode="select" selectedBidIds={winnerIds} onToggle={toggleWinner} />
        <SupplierCountIndicator selectedCount={winnerIds.length} maxParticipants={maxParticipants} />
      </Stack>

      <MinimumRequirementWarning
        notFulfilled={supplierNames(evaluation.bids, winnerIds, failsMinimumRequirement)}
        unassessed={supplierNames(evaluation.bids, winnerIds, hasUnassessedMinimumRequirements)}
      />

      <FormControl>
        <FormLabel>{t("Estimated contract value")}</FormLabel>
        <HStack>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            bg="smBackground"
            maxW="xs"
            placeholder="0"
          />
          <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} bg="smBackground" maxW="32">
            {Object.values(Currency).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </HStack>
      </FormControl>

      <FormControl>
        <FormLabel>{t("Internal note")}</FormLabel>
        <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} bg="smBackground" rows={2} />
        <FormHelperText>{t("Only visible to your organization.")}</FormHelperText>
      </FormControl>

      <Text fontSize="sm" color="smMuted">
        {t("You'll prepare and send award letters to each supplier after awarding.")}
      </Text>

    </Stack>
  );
};

const supplierNames = (
  bids: EvalBidDto[],
  winnerIds: string[],
  matches: (bid: EvalBidDto) => boolean,
): string[] =>
  bids.filter((bid) => winnerIds.includes(bid.bidId) && matches(bid)).map((bid) => bid.supplierName);

const MinimumRequirementWarning = ({
  notFulfilled,
  unassessed,
}: {
  notFulfilled: string[];
  unassessed: string[];
}) => {
  const { t } = useTranslation();
  if (notFulfilled.length === 0 && unassessed.length === 0) return null;
  return (
    <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Stack spacing="2">
        {notFulfilled.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fulfilled: {{suppliers}}", {
                suppliers: notFulfilled.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "A tender that does not meet an absolute minimum requirement must be rejected, not awarded. Review the minimum requirements before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
        {unassessed.length > 0 && (
          <Box>
            <AlertTitle>
              {t("Minimum requirements are not fully assessed: {{suppliers}}", {
                suppliers: unassessed.join(", "),
              })}
            </AlertTitle>
            <AlertDescription>
              {t(
                "You do not yet know whether the tender meets the minimum requirements. Assess them before you award.",
              )}
            </AlertDescription>
          </Box>
        )}
      </Stack>
    </Alert>
  );
};
